/*
 * Rounds list — schedule + configure rounds. Pairings live inside each
 * round detail; this page is just the round-level overview.
 *
 * Card layout, per design:
 *   [icon] DATE (small, dim)                              [STATUS]
 *          Course Name (hero — falls back to round name)
 *          Round name · Format · N matches · N pts
 *
 * The course is the headline because it's the single most identifying
 * detail of a round. The round's user-given name is secondary context.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Plus, Flag, ChevronRight } from 'lucide-react';

import { api } from '@/lib/api';
import { sessionDisplayLabel } from '@/lib/roundSchedule';
import { formatTeamCupPoints } from '@/lib/teamCupPoints';
import { useTournamentStore } from '@/store/tournamentStore';
import { AppShell } from '@/components/AppShell';
import { TeamAvatar } from '@/components/TeamAvatar';
import { formatLabel, RoundStatusPill, SCORING_MODE_LABELS } from './_shared';
import type { TournamentRoundListItem } from '@golf-app/shared';

export default function RoundsList() {
  const { eventId } = useParams<{ eventId: string }>();
  const { activeEvent, setActiveEvent } = useTournamentStore();

  const [rounds, setRounds] = useState<TournamentRoundListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Also hydrate the event so isOwner works on hard navigation / refresh.
  const fetchAll = useCallback(async () => {
    if (!eventId) return;
    setError(null);
    try {
      const [rs, ev] = await Promise.all([
        api.getTournamentRounds(eventId),
        activeEvent?.id === eventId ? Promise.resolve(activeEvent) : api.getTournament(eventId),
      ]);
      setRounds(rs);
      if (ev) setActiveEvent(ev);
    } catch (e: any) {
      setError(e?.message ?? "Couldn't load rounds");
    } finally {
      setLoading(false);
    }
  }, [eventId, activeEvent, setActiveEvent]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const isOwner = activeEvent?.id === eventId && activeEvent?.myRole === 'owner';

  // Block round creation while teams are uneven. A singles match-play round
  // can't pair up if one team has more players than the other. The user can
  // fix this from Teams & Captains by moving someone between teams.
  const teamSizes = (activeEvent?.teams ?? []).map((t) => ({
    team: t,
    count: (activeEvent?.players ?? []).filter(
      (p) => p.teamId === t.id && p.status !== 'removed' && p.roleType !== 'owner',
    ).length,
  }));
  const teamsBalanced = teamSizes.length === 0 ||
    teamSizes.every((s) => s.count === teamSizes[0].count);

  return (
    <AppShell back={{ to: `/app/tournaments/${eventId ?? ''}`, label: 'Event' }}>
      <section className="t-hero">
        <div className="t-hero-row">
          <div>
            <h1>Rounds</h1>
            <p>Schedule and configure rounds for this event. Each round can use a different course, tee, and format.</p>
          </div>
          {/* Round creation is always allowed once you're the owner. The
              round structure (course, tee, format, date) is independent of
              team balance — owners can pre-load rounds while team setup
              is still in flight, and come back to set pairings later when
              the rosters are final. */}
          {isOwner && (
            <Link to={`/app/tournaments/${eventId}/rounds/new`} className="t-btn t-btn-primary">
              <Plus /> Create Round
            </Link>
          )}
        </div>
      </section>


      {isOwner && !teamsBalanced && (
        <div
          className="t-error"
          style={{
            borderColor: 'var(--gold)',
            color: 'var(--gold)',
            background: 'rgba(212, 175, 55, 0.08)',
          }}
        >
          <strong>Heads up: teams aren't balanced yet.</strong>{' '}
          {teamSizes.map((s, i) => (
            <span key={s.team.id}>
              {s.team.name} has {s.count}
              {i < teamSizes.length - 1 ? ', ' : '. '}
            </span>
          ))}
          You can still set up the rounds here (course, tees, format, date). Come back to build pairings once the rosters are final.
        </div>
      )}

      {error && <div className="t-error">{error}</div>}

      {loading ? (
        <div className="t-loading"><div className="spinner" /></div>
      ) : rounds.length === 0 ? (
        <div className="t-empty">
          <div className="t-empty-icon"><Flag /></div>
          <div className="t-empty-title">No rounds yet</div>
          <div className="t-empty-hint">
            {isOwner ? 'Create your first round to get started.' : "The organizer hasn't added rounds yet."}
          </div>
        </div>
      ) : (
        <div className="t-list">
          {rounds.map((r) => {
            // Course is the headline. If no course is set yet, fall back to
            // the round's user-given name so the card never feels empty.
            const hero = r.courseNameSnapshot ?? r.name;
            // Round name becomes secondary context — but only show it when
            // it's not redundant with the hero.
            const secondaryName = r.courseNameSnapshot && r.name && r.name !== r.courseNameSnapshot
              ? r.name
              : null;
            const subBits = [
              secondaryName,
              formatLabel(r.formatType),
              SCORING_MODE_LABELS[r.scoringMode],
              r.scoringMode === 'stableford'
                ? 'Event-wide team total'
                : `${r.matchCount} match${r.matchCount !== 1 ? 'es' : ''}`,
              r.scoringMode === 'stableford'
                ? null
                : `${formatTeamCupPoints(r.totalPointsAvailable)} pts`,
            ].filter(Boolean);
            const dateLabel = r.scheduledStartAt ? formatRoundDate(r.scheduledStartAt) : null;
            // Show the team-vs-team score line only after at least one
            // match has been finalized. Otherwise the line is just "0 — 0"
            // for every draft round, which is noise.
            const showSummary = r.scoringMode !== 'stableford'
              && (r.matchesFinal ?? 0) > 0
              && (r.teamSummary?.length ?? 0) >= 2;
            return (
              <Link key={r.id} to={`/app/tournaments/${eventId}/rounds/${r.id}`} className="t-row">
                <div className="t-row-icon t-row-icon-green"><Flag /></div>
                <div className="t-row-body">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: 'var(--text-mut)' }}>
                      {dateLabel ?? 'Unscheduled'}
                    </span>
                    <RoundStatusPill status={r.status} />
                  </div>
                  <div className="t-row-name" style={{ marginTop: 2 }}>{hero}</div>
                  <div className="t-row-sub" style={{ marginTop: 2 }}>
                    {subBits.join('  ·  ')}
                  </div>
                  {showSummary && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                      {r.teamSummary.map((team, i) => (
                        <div key={team.teamId} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                          <TeamAvatar team={team} size={14} />
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-sec)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {team.name}
                          </span>
                          <span
                            style={{
                              fontSize: 14, fontWeight: 900,
                              color: team.primaryColor ?? 'var(--text)',
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {formatTeamCupPoints(team.points)}
                          </span>
                          {i < r.teamSummary.length - 1 && (
                            <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 800, color: 'var(--text-mut)' }}>—</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="t-row-chevron"><ChevronRight /></div>
              </Link>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}

/** Format a round's scheduled start as a short, readable date label.
 *  Examples: "Sat · Jun 7" (current year) or "Jun 7, 2027" (other years).
 *  Appends (Morning) or (Afternoon) when the round's scheduled time
 *  clearly falls in one bucket — this disambiguates two rounds on the
 *  same day, which is the standard Friday-Saturday cup format.
 *  Times in the wee hours (midnight / unset) get no suffix. */
function formatRoundDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  const weekday = d.toLocaleDateString(undefined, { weekday: 'short' });
  const month = d.toLocaleDateString(undefined, { month: 'short' });
  const day = d.getDate();
  const base = sameYear
    ? `${weekday} · ${month} ${day}`
    : `${month} ${day}, ${d.getFullYear()}`;
  // Morning/Afternoon comes from the shared schedule decoder — only
  // rounds where a session was explicitly picked get a label.
  const session = sessionDisplayLabel(iso);
  return session ? `${base} (${session})` : base;
}
